/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react/no-this-in-sfc */
import * as d3 from 'd3';
import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';

import theme from 'theme';
import { getCurrentUtcDate } from 'utils/dateTimeHelpers';

import {
  d3ApplyAxisTypogaphy,
  d3AxisRemoveCornerTicks,
  d3ComputeBarChartMeasurementsXAxis,
  d3EnterExitBars,
  d3SetTickAlignment,
  d3SetTickStyle,
  Prop2d3GraphPadding,
  removeTickLines,
  roundUpMag,
} from '../_common/d3GraphHelpers';
import { D3GraphPaddingProp, DiseaseActivityDatumType } from '../_common/DiseaseActivityModels';
import {
  BigGraphLoadingSkeleton,
  computeIfNeedToShowMovingAverage,
  computeMovingAverage,
  IterateWithinDateWindow,
  PopupArrowBottom,
} from '../_common/graphUtils';

// //=====================================================================================================================================
// const Date30DaysAgo = getMomentAgo('last-30-days').toDate();
// //=====================================================================================================================================

// =====================================================================================================================================
const DEBUG_SVG = 0;
const DEBUG_GAPS = 0;
const DEBUG_HOVER = 0;
const useViewboxToFit100PercentWidth = false;
const DefaultMaxDate = getCurrentUtcDate();
const BarThicknesses = [1, 14];
const barsBgColor = theme.colors.deepSea10;
const barsBgHighlightColor = theme.colors.sea20;
const axisColor = theme.colors.stone30;
const axisLabelColor = theme.colors.stone50;
const popupUpperBg = 'rgba(37, 40, 56, 0.8)'; // NOTE: stone100 (#252838) + opacity: 0.8
const AvgCurveColor = '#36639F';
const defaultMaxYValue = 10;
const dashHoverStrokeWidth = 1;
const showRunningAvgThreshold = 0.8;
const movingAverageWindow = 7;
const popupTopOffset = '-11px';
const popupUpperW = 130;
const popupUpperH = 42;
const popupPaddingLower = '10px';
const popupPaddingUpper = '8px';
// =====================================================================================================================================

type BdDiseaseActivityGraphSeriesColors = { outside: string; inside: string; hover: string };
type BdDiseaseActivityGraphProps = {
  width: number;
  height: number;
  padding?: D3GraphPaddingProp;
  graphData: DiseaseActivityDatumType[];
  isLoading?: boolean;
  minDate: Date;
  maxDate?: Date;
  aggregationDays?: number;
  highlightStartDate?: Date;
  highlightEndDate?: Date;
  disablePopupUpper?: boolean;
  onlyWithUpdatesMakeActiveOnClick?: boolean;
  colorSeries1?: BdDiseaseActivityGraphSeriesColors;
  colorSeries2?: BdDiseaseActivityGraphSeriesColors;
  renderPopupLower: (
    item: DiseaseActivityDatumType,
    isActive: boolean,
    callbackClosePopup: () => void
  ) => React.ReactElement;
  renderPopupUpper: (item: DiseaseActivityDatumType) => React.ReactElement;
  onShowingAverageChange?: (flag: boolean) => void;
};

export const BdDiseaseActivityGraph: React.FC<BdDiseaseActivityGraphProps> = ({
  width: widthSVG,
  height: heightSVG,
  padding: paddingProp = 10,
  graphData,
  isLoading = false,
  minDate,
  maxDate = DefaultMaxDate,
  aggregationDays = 1,
  highlightStartDate,
  highlightEndDate,
  disablePopupUpper,
  onlyWithUpdatesMakeActiveOnClick,
  colorSeries1 = {
    outside: theme.colors.sea30,
    inside: theme.colors.sea50,
    hover: theme.colors.stone100,
  },
  colorSeries2 = {
    outside: theme.colors.clay80,
    inside: theme.colors.clay80,
    hover: theme.colors.clay100,
  },
  renderPopupLower,
  renderPopupUpper,
  onShowingAverageChange,
}) => {
  const refRoot = React.useRef();
  const refSvg = React.useRef();

  const [datumHover, setDatumHover] = useState<DiseaseActivityDatumType>(null);
  const [datumActive, setDatumActive] = useState<DiseaseActivityDatumType>(null);

  // ------------------------------------------------------------------------------------------------------------------------------------- Memos
  const padding = useMemo(() => Prop2d3GraphPadding(paddingProp), [paddingProp]);
  // TODO: rename graphWidth/graphHeight
  const width = widthSVG - padding.left - padding.right;
  const height = heightSVG - padding.top - padding.bottom;
  const graphDataNoGaps = useMemo(
    () => IterateWithinDateWindow(graphData || [], minDate, maxDate, aggregationDays, true),
    [graphData, minDate, maxDate, aggregationDays]
  );
  const graphDataWithGaps = useMemo(
    () => IterateWithinDateWindow(graphData || [], minDate, maxDate),
    [graphData, minDate, maxDate]
  );
  const showAvgCurve = useMemo(
    () =>
      computeIfNeedToShowMovingAverage(
        graphDataWithGaps,
        minDate,
        maxDate,
        showRunningAvgThreshold,
        aggregationDays
      ),
    [graphDataWithGaps, minDate, maxDate, aggregationDays]
  );
  const graphDataAvg: DiseaseActivityDatumType[] = useMemo(
    () => showAvgCurve && computeMovingAverage(graphDataWithGaps, movingAverageWindow),
    [graphDataWithGaps, showAvgCurve]
  );
  const maxYValue = useMemo(
    () =>
      graphDataNoGaps && graphDataNoGaps.length
        ? roundUpMag(d3.max(graphDataNoGaps, d => Math.max(d.series1 ?? 0, d.series2 ?? 0)))
        : defaultMaxYValue,
    [graphDataNoGaps]
  );
  const [barThicknessMin, barThicknessMax] = BarThicknesses;
  const [barWidth, barMargin, nDays] = useMemo(
    () =>
      d3ComputeBarChartMeasurementsXAxis(
        minDate,
        maxDate,
        width,
        barThicknessMin,
        barThicknessMax,
        aggregationDays
      ),
    [aggregationDays, barThicknessMax, barThicknessMin, maxDate, minDate, width]
  );
  const scaleX = useMemo(
    () =>
      d3
        .scaleTime()
        .domain([minDate, maxDate])
        .range([0, width - barWidth]),
    [barWidth, maxDate, minDate, width]
  );
  const scaleY = useMemo(
    () => d3.scaleLinear().domain([0, maxYValue]).range([height, 0]).clamp(true),
    [height, maxYValue]
  );
  const scaleYi = useMemo(
    () => d3.scaleLinear().domain([0, maxYValue]).range([0, height]).clamp(true),
    [height, maxYValue]
  );
  const highlightStart = useMemo(
    () =>
      highlightStartDate
        ? moment.max(moment(highlightStartDate || minDate), moment(minDate)).toDate()
        : null,
    [highlightStartDate, minDate]
  );
  const highlightEnd = useMemo(
    () => moment.min(moment(highlightEndDate || maxDate), moment(maxDate)).toDate(),
    [highlightEndDate, maxDate]
  );

  // ------------------------------------------------------------------------------------------------------------------------------------- CALLBACKS

  useEffect(() => onShowingAverageChange && onShowingAverageChange(showAvgCurve), [showAvgCurve]);

  // ------------------------------------------------------------------------------------------------------------------------------------- CREATE
  useEffect(() => {
    const svg = d3.select(refSvg.current);
    DEBUG_SVG &&
      svg.append('rect').attr('width', '100%').attr('height', '100%').attr('fill', '#e394e3');
    svg
      .append('g')
      .attr('class', 'chart-root')
      .call(chartRoot => {
        chartRoot
          .append('g')
          .attr('class', 'bars-bg')
          .call(chartRoot => {
            chartRoot.append('g').attr('class', 'bars-bg');
            chartRoot.append('g').attr('class', 'chart-bars');
          });
        chartRoot.append('g').attr('class', 'chart-bars');
      });
    svg.append('g').attr('class', 'x-axis');
    svg.append('g').attr('class', 'y-axis');
    svg
      .append('g')
      .attr('class', 'chart-fg-loading')
      .attr(
        'transform',
        `translate(${padding.left},${padding.top + height - 104}), scale(${width / 261.0}, 1)`
      )
      .html(BigGraphLoadingSkeleton)
      .style('display', 'none');
    svg
      .append('g')
      .attr('class', 'hover-goodies')
      .attr('transform', `translate(${padding.left},${padding.top})`); // NOTE: This makes hover-goodies on top of the axes/chart
    svg
      .select('.hover-goodies')
      .append('path')
      .attr('class', 'popup-lower-arrow')
      .attr('d', PopupArrowBottom)
      .attr('fill', colorSeries1.hover)
      .style('display', 'none');
  }, []);

  // ------------------------------------------------------------------------------------------------------------------------------------- UPDATES
  useEffect(() => {
    const svg = d3.select(refSvg.current);
    svg.select('.chart-root').attr('transform', `translate(${padding.left},${padding.top})`);
    svg.select('.x-axis').attr('transform', `translate(${padding.left}, ${height + padding.top})`);
    svg.select('.y-axis').attr('transform', `translate(${padding.left + width}, ${padding.top})`);
  }, [width, height, padding]);

  // -------------------------------------------------------------------------------------------------------------------------------------
  useEffect(() => {
    const svg = d3.select(refSvg.current);
    svg.select('.chart-fg-loading').style('display', isLoading ? 'block' : 'none');
    svg.select('.chart-root').style('display', !isLoading ? 'block' : 'none');
    svg.select('.popup-lower-arrow').style('display', !isLoading ? 'block' : 'none');
  }, [isLoading]);

  // -------------------------------------------------------------------------------------------------------------------------------------
  useEffect(() => {
    const svg = d3.select(refSvg.current);
    const gBarsBg = svg.select('.bars-bg');

    gBarsBg.selectAll('*').remove();
    gBarsBg
      .append('rect')
      .attr('class', 'bg-generic')
      .attr('width', width)
      .attr('height', height)
      .attr('fill', barsBgColor);
    if (highlightStart) {
      gBarsBg
        .append('rect')
        .attr('class', 'highlight-rect')
        .attr('width', 0)
        .attr('height', height)
        .attr('fill', barsBgHighlightColor)
        .attr('x', scaleX(highlightStart))
        .attr('width', Math.max(0, scaleX(highlightEnd) - scaleX(highlightStart) + barWidth));
    }
  }, [highlightStart, highlightEnd, maxDate, minDate, width, height, scaleX, barWidth]);

  // -------------------------------------------------------------------------------------------------------------------------------------
  useEffect(() => {
    const svg = d3.select(refSvg.current);

    const placeholderAxisX = svg.select('.x-axis');
    placeholderAxisX.selectAll('*').remove();
    placeholderAxisX
      .call(
        d3
          .axisBottom(scaleX)
          .ticks(nDays > 90 ? 2 : d3.timeMonth.every(1))
          .tickFormat(d3.timeFormat('%b %Y')) // LINK: https://github.com/d3/d3-time-format#locale_format
      )
      .attr('color', axisColor)
      .call(d3ApplyAxisTypogaphy(axisLabelColor))
      .call(d3SetTickAlignment('start')) // CODE: bafadd50: all tick labels left align
      .call(d3SetTickStyle({ thickness: 2, length: 4 }))
      .call(
        d3AxisRemoveCornerTicks({
          axisType: 'x-axis',
          axisLength: width,
          axisColor: axisColor,
        })
      );

    const placeholderAxisY = svg.select('.y-axis');
    placeholderAxisY.selectAll('*').remove();
    placeholderAxisY
      .call(d3.axisRight(scaleY).ticks(2).tickFormat(d3.format(',~s')))
      .attr('color', axisColor)
      .call(d3ApplyAxisTypogaphy(axisLabelColor))
      .call(
        d3AxisRemoveCornerTicks({
          axisType: 'y-axis',
          axisLength: height,
          axisColor: axisColor,
        })
      )
      .call(removeTickLines({ textNewX: 4 }));
  }, [nDays, scaleX, scaleY, width, height]);

  // -------------------------------------------------------------------------------------------------------------------------------------
  useEffect(() => {
    const svg = d3.select(refSvg.current);
    const root = d3.select(refRoot.current);

    const [bars, barsEntred] = d3EnterExitBars(
      svg.select<SVGGElement>('.chart-bars'),
      graphDataNoGaps,
      'g'
    );

    barsEntred
      .append('line')
      .attr('class', 'hover-dashes')
      .attr('stroke-width', dashHoverStrokeWidth)
      .attr('stroke-dasharray', `${dashHoverStrokeWidth},${dashHoverStrokeWidth * 3}`)
      .attr('stroke', colorSeries1.hover)
      .style('display', 'none');
    barsEntred.append('rect').attr('class', 'series1');
    barsEntred.append('rect').attr('class', 'series2');
    barsEntred.append('rect').attr('class', 'mask').attr('fill', 'transparent');

    bars.classed('ghost', d => d.isGhost);

    bars
      .select('.hover-dashes')
      .attr('x1', d => scaleX(d.date) + dashHoverStrokeWidth / 4 + barWidth / 2)
      .attr('y1', d => scaleY(d.series1))
      .attr('x2', d => scaleX(d.date) + dashHoverStrokeWidth / 4 + barWidth / 2)
      .attr('y2', d => scaleY(maxYValue));

    function seriesFillFn(colorScheme: BdDiseaseActivityGraphSeriesColors) {
      return (d: DiseaseActivityDatumType) =>
        highlightStart && highlightStart <= d.date && d.date <= highlightEnd
          ? colorScheme.inside
          : colorScheme.outside;
    }

    const barsSeries1 = bars
      .select('.series1')
      .attr('x', d => scaleX(d.date))
      .attr('y', d => scaleY(d.series1 || 0))
      .attr('height', d => scaleYi(d.series1 || 0))
      .attr('width', barWidth)
      .attr('fill', seriesFillFn(colorSeries1));

    const barsSeries2 = bars
      .select<SVGGElement>('.series2')
      .attr('x', d => scaleX(d.date))
      .attr('y', d => scaleY(d.series2 || 0))
      .attr('height', d => scaleYi(d.series2 || 0))
      .attr('width', barWidth)
      .attr('fill', seriesFillFn(colorSeries2));

    const barsHoverMask = bars
      .select<SVGGElement>('.mask')
      .attr('x', d => scaleX(d.date) - barMargin / 2)
      .attr('y', d => scaleYi(0))
      .attr('height', d => scaleYi(maxYValue))
      .attr('width', barWidth + barMargin + 1);

    DEBUG_GAPS && bars.selectAll('.ghost .mask').attr('fill', 'gray');

    let datumHover_: DiseaseActivityDatumType = null;
    let datumActive_: DiseaseActivityDatumType = null;
    barsHoverMask
      .on('mouseover', function (d, i) {
        setDatumHover((datumHover_ = d));
        d3.select(this.parentElement).select('.hover-dashes').style('display', 'block');
        d3.select(this.parentElement).select('.series1').attr('fill', colorSeries1.hover);
        d3.select(this.parentElement).select('.series2').attr('fill', colorSeries2.hover);
      })
      .on('mouseout', function (d, i) {
        d3.select(this.parentElement).select('.hover-dashes').style('display', 'none');
        d3.select(this.parentElement).select('.series1').attr('fill', seriesFillFn(colorSeries1));
        d3.select(this.parentElement).select('.series2').attr('fill', seriesFillFn(colorSeries2));
      })
      .on('click', function (d, i) {
        svg.selectAll('.active-goodie').remove();
        setDatumActive(
          (datumActive_ =
            d !== datumActive_ && (d.updatesCount || !onlyWithUpdatesMakeActiveOnClick) ? d : null)
        );
        if (datumActive_) {
          d3.select(this.parentElement)
            .append('rect')
            .attr('class', 'active-goodie')
            .attr('x', scaleX(datumActive_.date))
            .attr('y', scaleY(datumActive_.series1 || 0))
            .attr('height', scaleYi(datumActive_.series1 || 0))
            .attr('width', barWidth)
            .attr('fill', colorSeries1.hover);
          d3.select(this.parentElement)
            .append('rect')
            .attr('class', 'active-goodie')
            .attr('x', scaleX(datumActive_.date))
            .attr('y', scaleY(datumActive_.series2 || 0))
            .attr('height', scaleYi(datumActive_.series2 || 0))
            .attr('width', barWidth)
            .attr('fill', colorSeries2.hover);
        }
      });

    svg.select('.chart-root').on('mouseleave', function (d, i) {
      setDatumHover((datumHover_ = null));
    });
    root.on('blur', function (d, i) {
      svg.selectAll('.active-goodie').remove();
      setDatumHover((datumHover_ = null));
      setDatumActive((datumActive_ = null));
    });
  }, [
    barMargin,
    barWidth,
    graphDataNoGaps,
    highlightStart,
    highlightEnd,
    maxYValue,
    scaleX,
    scaleY,
  ]);

  // -------------------------------------------------------------------------------------------------------------------------------------
  useEffect(() => {
    const svg = d3.select(refSvg.current);

    svg.selectAll('.avg-curve').remove();
    graphDataAvg &&
      svg
        .select('.chart-root')
        .append('path')
        .attr('class', 'avg-curve')
        .datum(graphDataAvg)
        .attr('fill', 'none')
        .style('pointer-events', 'none')
        .attr('stroke', showAvgCurve ? AvgCurveColor : 'transparent') // sea90
        .attr('stroke-width', 1.5)
        .attr(
          'd',
          d3
            .line<DiseaseActivityDatumType>()
            .curve(d3.curveCardinal)
            .x(d => scaleX(d.date) + barWidth / 2)
            .y(d => scaleY(d.series1))
        );
  }, [barWidth, graphDataAvg, maxYValue, scaleX, scaleY, showAvgCurve]);

  // -------------------------------------------------------------------------------------------------------------------------------------
  useEffect(() => {
    const svg = d3.select(refSvg.current);
    const root = d3.select(refRoot.current);

    const targetDatum = datumActive || datumHover;
    d3.select(refRoot.current)
      .select('.popup-lower')
      .style('display', targetDatum ? 'block' : 'none');
    if (targetDatum) {
      const xCoord = scaleX(targetDatum.date) + barWidth / 2;
      svg
        .select('.popup-lower-arrow')
        .attr('transform', `translate(${xCoord - 7}, ${scaleY(0) + 2})`)
        .style('display', 'block');
    } else {
      svg.select('.popup-lower-arrow').style('display', 'none');
    }

    if (datumHover && datumActive !== datumHover) {
      const xCoord = scaleX(datumHover.date) + barWidth / 2;
      let leftStyleValue = xCoord + padding.left - popupUpperW / 2;
      if (leftStyleValue < 0) leftStyleValue = 0;
      else if (leftStyleValue + popupUpperW > widthSVG) leftStyleValue = widthSVG - popupUpperW;
      !disablePopupUpper &&
        root
          .select('.popup-upper')
          .style('display', !datumHover.isGhost && datumHover?.updatesCount > 0 ? 'block' : 'none')
          .style('left', `${leftStyleValue}px`);
    } else {
      root.select('.popup-upper').style('display', 'none');
    }
  }, [barWidth, datumActive, datumHover, padding.left, scaleX, scaleY, widthSVG]);

  const popupUpperContent = useMemo(
    () => (datumHover?.updatesCount > 0 ? renderPopupUpper(datumHover) : undefined),
    [datumHover, renderPopupUpper]
  );

  const clearDatumActive = () => {
    (refRoot.current as HTMLElement)?.blur();
    setDatumActive(null);
  };

  const popupLowerContent = useMemo(
    () =>
      datumActive
        ? renderPopupLower(datumActive, true, () => clearDatumActive())
        : datumHover
        ? renderPopupLower(datumHover, false, () => clearDatumActive())
        : undefined,
    [datumActive, datumHover, renderPopupLower]
  );

  // return <svg ref={ref} width={widthSVG} height={heightSVG} />;
  return (
    // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex, jsx-a11y/tabindex-no-positive
    <div ref={refRoot} style={{ height: `${heightSVG}px`, outline: 'none' }} tabIndex={1}>
      <div className="popup-upper-con" style={{ position: 'relative' }}>
        <div
          className="popup-upper"
          style={{
            display: 'none',
            position: 'absolute',
            zIndex: 10000,
            width: `${popupUpperW}px`,
            height: `${popupUpperH}px`,
            bottom: `${-padding.top}px`,
            background: popupUpperBg,
            borderRadius: '4px',
            padding: popupPaddingUpper,
          }}
        >
          {popupUpperContent}
        </div>
      </div>
      <svg
        ref={refSvg}
        width={useViewboxToFit100PercentWidth ? '100%' : widthSVG}
        height={heightSVG}
        style={{ outline: 'none' }}
      />
      <div className="popup-lower-con" style={{ position: 'relative' }}>
        <div
          className="popup-lower"
          style={{
            display: 'none',
            position: 'absolute',
            zIndex: 10000,
            width: '100%',
            top: popupTopOffset,
            background: colorSeries1.hover,
            borderRadius: '4px',
            padding: popupPaddingLower,
          }}
        >
          {popupLowerContent}
        </div>
      </div>
    </div>
  );
};
